import type { Field, FieldType } from '../../api/services'
import { UNSET, formatCount, formatIrr } from '../../format'

/**
 * The words for a capability's contract, and the values the Sandbox pre-fills.
 *
 * Everything here is keyed on a *field name* or a field *type* — never on a
 * capability code. That is deliberate: a client-side list of capabilities is
 * exactly how a dormant service ends up on a customer's screen after somebody
 * adds one to the server and forgets this file. The server decides which
 * services exist and which fields they take; this module only decides how to
 * say a field name in Persian and what an obviously-fake example looks like.
 *
 * A name this module has not been taught falls back to itself. A raw
 * `branch_code` beside a Persian label is visibly unfinished, which is better
 * than a confident mistranslation of somebody's bank record.
 */

/** The Persian name of a contract field, by the field's own name. */
const FIELD: Record<string, string> = {
  // --- identifiers a caller supplies ---
  national_code: 'کد ملی',
  national_id: 'شناسهٔ ملی',
  legal_id: 'شناسهٔ حقوقی',
  birth_date: 'تاریخ تولد',
  card_number: 'شمارهٔ کارت',
  iban: 'شماره شبا',
  account_number: 'شمارهٔ حساب',
  bank_code: 'کد بانک',
  mobile: 'شمارهٔ همراه',
  cheque_id: 'شناسهٔ صیادی چک',
  is_company: 'شخص حقوقی است',

  // --- what comes back ---
  matched: 'نتیجهٔ تطبیق',
  holder_name: 'نام دارنده',
  bank_name: 'نام بانک',
  branch_code: 'کد شعبه',
  branch_description: 'مشخصات شعبه',
  active: 'فعال بودن',
  alive: 'در قید حیات',
  owners: 'مالکان',
  first_name: 'نام',
  last_name: 'نام خانوادگی',
  father_name: 'نام پدر',
  gender_code: 'کد جنسیت',
  identifier: 'شناسه',
  image_base64: 'تصویر ثبت‌احوالی',
  address: 'نشانی',
  postal_code: 'کد پستی',
  city: 'شهر',
  province: 'استان',
  status: 'وضعیت',
  title: 'عنوان',
  name: 'نام',
  description: 'شرح',

  // --- the legal-person family ---
  //
  // Dormant when this module was written, sold since 2026-09-03. Without
  // these the detail pages of five capabilities showed `obligatory_signatories`
  // beside a Persian label, which the fallback permits but nobody would ship
  // for a family the customer is being charged for.
  company_type: 'نوع شخصیت حقوقی',
  registration_number: 'شمارهٔ ثبت',
  registration_date: 'تاریخ ثبت',
  dissolution_date: 'تاریخ انحلال',
  board_members: 'اعضای هیئت‌مدیره',
  shareholders: 'سهام‌داران',
  allowed_topics: 'موضوع فعالیت',
  signature_full_text: 'متن کامل دارندگان حق امضا',
  obligatory_signatories: 'صاحبان امضای الزامی',
  normal_signatories: 'صاحبان امضای عادی',
  joint_signatories: 'صاحبان امضای مشترک',
  newspaper_date: 'تاریخ روزنامهٔ رسمی',
  newsletter_date: 'تاریخ آگهی',
  notices: 'آگهی‌های روزنامهٔ رسمی',

  // --- cheque and Sayad ---
  count: 'تعداد',
  amount_irr: 'مبلغ',
  total_amount_irr: 'مبلغ کل',
  total_bounced_amount_irr: 'مبلغ کل برگشتی',
  bounced_amount_irr: 'مبلغ برگشتی',
  bounce_date: 'تاریخ برگشت',
  bounced_branch_code: 'کد شعبهٔ برگشت‌زننده',
  dishonor_reason: 'علت برگشت',
  dishonoring_branch_name: 'نام شعبهٔ برگشت‌زننده',
  cheque_date: 'تاریخ چک',
  cheque_number: 'شمارهٔ چک',
  cheque_type: 'نوع چک',
  color_code: 'کد رنگ',
  color_label: 'رنگ اعتباری',
  serial_number: 'سری',
  series_number: 'سریال',
  issued_date: 'تاریخ صدور',
  expiration_date: 'تاریخ انقضا',
}

/** The Persian name of a field type. */
const TYPE: Record<FieldType, string> = {
  string: 'متن',
  integer: 'عدد صحیح',
  number: 'عدد',
  boolean: 'بله یا خیر',
  list: 'فهرست',
  object: 'شیء',
}

export function fieldLabel(name: string): string {
  return FIELD[name] ?? name
}

export function typeLabel(type: FieldType): string {
  return TYPE[type] ?? type
}

/**
 * The example the Sandbox form opens with.
 *
 * Every value is transparently synthetic — all zeros, or a date nobody was
 * born on. A plausible-looking national code in a demo form is a real person's
 * identity being queried by somebody who was only clicking around, and the
 * Sandbox spends the customer's wallet for the privilege.
 */
const EXAMPLE: Record<string, string> = {
  national_code: '0000000000',
  national_id: '00000000000',
  legal_id: '00000000000',
  birth_date: '1370/01/01',
  card_number: '0000000000000000',
  iban: 'IR000000000000000000000000',
  account_number: '0000000000',
  bank_code: '000',
  mobile: '09000000000',
  cheque_id: '00000000000000000000',
}

/** The pre-filled value for one request field, in the form's own terms. */
export function exampleFor(field: Field): string | boolean {
  if (field.type === 'boolean') return false
  if (field.type === 'integer' || field.type === 'number') return '0'
  return EXAMPLE[field.name] ?? '000000'
}

/**
 * Whether this capability answers a yes/no matching question.
 *
 * Derived from the contract rather than from a list of codes, so it is true for
 * exactly the capabilities whose result is a `matched` boolean and cannot drift
 * from what the server actually returns.
 */
export function isMatchContract(result: readonly Field[]): boolean {
  return result.some((field) => field.name === 'matched' && field.type === 'boolean')
}

/** True/false for a `matched` field, said as the answer it is. */
export function matchedLabel(value: boolean): string {
  return value ? 'تطبیق دارد' : 'تطبیق ندارد'
}

/**
 * One result value as text.
 *
 * Money is the reason this exists: an amount arrives as an integer number of
 * rials and printing it raw beside a Persian label reads as a count. Anything
 * whose name ends in `_irr` is money and is formatted as money.
 */
export function formatResultValue(name: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return UNSET.amount
  if (typeof value === 'boolean') {
    if (name === 'matched') return matchedLabel(value)
    return value ? 'بله' : 'خیر'
  }
  if (typeof value === 'number') {
    if (name.endsWith('_irr') && Number.isInteger(value)) return formatIrr(value)
    return formatCount(value)
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return UNSET.amount
    return value.map((item) => (typeof item === 'object' ? '' : String(item))).join('، ')
  }
  return String(value)
}

/** The field whose value is a registry photograph, rendered as an image. */
export const IMAGE_FIELD = 'image_base64'
